import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useLocationStore } from '@/features/location/store/location-store';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { chargePointsApi } from '../api/charge-points-api';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '../store/charge-points-store';
import { MIN_ZOOM, MARKER_MIN_ZOOM } from '@/utils/constants';
import type { LatLngBounds } from '@/types/global';

const MAX_MERCATOR_LAT = 85.0511;

type ChargePointsQueryKey = ['charge-points', LatLngBounds, number, string];

/**
 * Bounds to fetch for the viewport: the most recent successfully fetched area
 * at this zoom + filters that already covers it (cache hit, no request), else
 * the viewport snapped to tiles. Both `useChargePoints` callers read the same
 * cache, so they always agree on the key.
 */
export function pickFetchBounds(
  queryClient: QueryClient,
  viewport: LatLngBounds,
  zoom: number,
  filtersKey: string
): LatLngBounds {
  const covering = queryClient
    .getQueryCache()
    .findAll({
      queryKey: ['charge-points'],
      predicate: (q) => q.state.status === 'success',
    })
    .filter((q) => {
      const [, b, z, f] = q.queryKey as ChargePointsQueryKey;
      return (
        z === zoom &&
        f === filtersKey &&
        b.north >= viewport.north &&
        b.south <= viewport.south &&
        b.east >= viewport.east &&
        b.west <= viewport.west
      );
    })
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];

  return covering
    ? (covering.queryKey as ChargePointsQueryKey)[1]
    : snapBoundsToTiles(viewport, zoom);
}

/**
 * Snap viewport bounds outward to whole Web Mercator tiles (256px at `zoom`).
 * Gives fetched areas a margin of up to one tile per side, so small pans stay
 * inside an already-fetched area (see `pickFetchBounds`) and the revealed
 * edge already has pins.
 */
export function snapBoundsToTiles(
  bounds: LatLngBounds,
  zoom: number
): LatLngBounds {
  const n = 2 ** zoom;
  const toX = (lng: number) => ((lng + 180) / 360) * n;
  const fromX = (x: number) => (x / n) * 360 - 180;
  const toY = (lat: number) => {
    const r =
      (Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat)) * Math.PI) /
      180;
    return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n;
  };
  const fromY = (y: number) =>
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;

  return {
    north: fromY(Math.max(0, Math.floor(toY(bounds.north)))),
    south: fromY(Math.min(n, Math.ceil(toY(bounds.south)))),
    east: fromX(Math.ceil(toX(bounds.east))),
    west: fromX(Math.floor(toX(bounds.west))),
  };
}

/**
 * Hook to fetch charge points for the current map viewport
 */
export function useChargePoints() {
  const visibleRegion = useLocationStore((s) => s.visibleRegion);
  const zoomLevel = useLocationStore((s) => s.zoomLevel);

  // Charge-sessions mode renders live session markers fed only by postMessage
  // and must never touch the charge-point path (BIZOPS-1588). The map already
  // mounts only the session layer in this mode, but `LoadingLine` calls this
  // hook unconditionally from ResponsiveLayout (rendered in both modes), so
  // gating `enabled` here keeps the `/charge_points/map` fetch from firing for
  // EVERY caller — a stray 401 there would silently re-mint a guest token.
  const [searchParams] = useSearchParams();
  const isChargeSessionsMode = searchParams.get('mapType') === 'chargeSessions';

  // Subscribe to all filter values individually to ensure reactivity
  // These subscriptions trigger re-renders when filters change
  useFilterStore((s) => s.showOnlyAvailable);
  useFilterStore((s) => s.showAutoCharge);
  useFilterStore((s) => s.showSlow);
  useFilterStore((s) => s.showFast);
  useFilterStore((s) => s.showUltraFast);
  useFilterStore((s) => s.selectedTeamIds);
  useFilterStore((s) => s.selectedOperatorIds);
  useFilterStore((s) => s.maxPricePerKwh);
  const getApiFilters = useFilterStore((s) => s.getApiFilters);

  const mergeChargePoints = useChargePointsStore(
    (s: ChargePointsState) => s.mergeChargePoints
  );
  const setLoadingMap = useChargePointsStore(
    (s: ChargePointsState) => s.setLoadingMap
  );

  // Compute filters from current state
  const filters = getApiFilters();

  // Vector maps report fractional zoom; round it so pinch/trackpad zoom
  // doesn't mint a new cache key per frame. Clamp to the API minimum.
  const zoom = Math.max(Math.round(zoomLevel), MIN_ZOOM);
  const filtersKey = JSON.stringify(filters);
  const queryClient = useQueryClient();
  const bounds = visibleRegion
    ? pickFetchBounds(queryClient, visibleRegion, zoom, filtersKey)
    : null;

  const query = useQuery({
    queryKey: ['charge-points', bounds, zoom, filtersKey],
    queryFn: async ({ signal }) => {
      if (!bounds) {
        throw new Error('No visible region available');
      }

      setLoadingMap(true);
      try {
        const data = await chargePointsApi.getChargePointsForMap({
          bounds,
          // Derive the center from the snapped bounds so the request is fully
          // determined by the query key (cache entries stay consistent).
          center: {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2,
          },
          zoom,
          filters,
          signal,
        });

        // Merge new data into cache (keeps existing pins visible during load)
        mergeChargePoints(data.single_list, data.cluster_list);

        return data;
      } finally {
        setLoadingMap(false);
      }
    },
    enabled: !isChargeSessionsMode && !!bounds && zoomLevel >= MARKER_MIN_ZOOM,
    // Availability changes, but not second-to-second: reuse a tile area for
    // 30s, then show cached pins instantly while refetching in the background.
    // Filter changes are part of the key, so they always fetch fresh.
    staleTime: 30_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData, // Keep old pins visible while loading new ones
  });

  return query;
}
